import { Component } from '@theme/component';
import { getViewParameterValue } from '@theme/utilities';

function setFamilyDocumentTitle(title) {
    const t = (title || '').trim();
    if (!t) return;

    if (document.title !== t) {
        document.title = t;
    }

    const titleEl = document.querySelector('head > title');
    if (titleEl && titleEl.textContent !== t) {
        titleEl.textContent = t;
    }
}

const URL_PARAM_CHILD = 'fp_child';
const URL_PREFIX = 'cp_';

class CustomPicker extends Component {
    #options = [];
    #matrix = new Map();
    #selected = new Map();
    #mainUrl = '';
    #baseTitle = '';
    #abortController = null;
    #boundClick = null;
    #boundGuardReady = null;
    #cache = new Map();
    #specificationsMap = {};
    #baseSpecificationsHtml = '';
    #instructionsMap = {};
    #baseInstructionsHtml = '';
    #isNavigatingToChild = false;

    connectedCallback() {
        super.connectedCallback();

        if (!this.closest('[data-family-guard-scope]')) return;

        this.#mainUrl = (this.dataset.mainUrl || '').trim() || window.location.pathname;
        this.#baseTitle = (this.dataset.baseTitle || '').trim();

        if (this.#redirectLegacyFpChildUrl()) return;

        setFamilyDocumentTitle(this.#baseTitle);

        this.#specificationsMap = this.#parseSpecifications(this.#getSpecificationsJson());
        this.#captureBaseSpecifications();

        this.#instructionsMap = this.#parseInstructions(this.#getInstructionsJson());
        this.#captureBaseInstructions();

        this.hydrate('connectedCallback');

        if (!this.#boundClick) {
            this.#boundClick = this.#onClick.bind(this);
            this.addEventListener('click', this.#boundClick);
        }

        // The guard fires this once its click-time enforcement is installed. Until
        // then the soft (clickable) lock cannot be applied — re-settle so an
        // unfinished selection reads as inactive but still explains itself.
        // Bound once and removed on disconnect: a section morph re-runs
        // connectedCallback, and an un-deduped listener would leave detached
        // instances mutating buttons on every future event.
        if (!this.#boundGuardReady) {
            this.#boundGuardReady = () => this.#refreshPurchaseState();
            document.addEventListener('family:guard-ready', this.#boundGuardReady);
        }

        this.applyStateFromUrl({ replace: true });

        // Settle purchase state on load. applyStateFromUrl returns early when the URL
        // carries no picker state, so without this a family parent would render with
        // a live Add to Cart and nothing selected.
        //
        // History: this line was added, reverted (2026-08-09), and deliberately
        // restored (2026-08-10). The revert was correct at the time — it greyed the
        // button out with no explanation. It is back because the button is now SOFT
        // locked: styled inactive but still clickable, so clicking it says "Please
        // select all options first". That was the missing half.
        this.#refreshPurchaseState();

        if (!this.#baseTitle) {
            console.warn('[CustomPicker] dataset.baseTitle is empty');
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback?.();
        if (this.#boundClick) {
            this.removeEventListener('click', this.#boundClick);
            this.#boundClick = null;
        }
        if (this.#boundGuardReady) {
            document.removeEventListener('family:guard-ready', this.#boundGuardReady);
            this.#boundGuardReady = null;
        }
        this.#abortController?.abort();
    }

    #getRoot() {
        return this.closest('[data-family-guard-scope]') || this;
    }

    hydrate(reason = '') {
        const optionsRaw = this.#getOptionsJson();
        const matrixRaw = this.#getMatrixJson();

        this.#options = this.#parseOptions(optionsRaw);
        this.#options = this.#options.filter((opt) => Array.isArray(opt?.values) && opt.values.length > 0);

        this.#matrix = this.#parseMatrix(matrixRaw);

        this.#selected = new Map();
        this.#options.forEach((opt) => {
            const v = (opt.current || '').trim();
            if (v) this.#selected.set(opt.key, v);
        });

        this.#syncActiveUI();
        this.#updateAvailability();
        this.#syncLineItemProperties();

        this.#setReadyState({ revealWarnings: false });
        this.#emitChange(`hydrate:${reason}`);

        this.#installTooltips();
        setFamilyDocumentTitle(this.#baseTitle);
    }

    applyStateFromUrl({ replace = false } = {}) {
        const u = new URL(window.location.href);

        let changed = false;

        this.#options.forEach((opt) => {
            const param = URL_PREFIX + String(opt.mf_key || '').trim();
            if (!param || param === URL_PREFIX) return;

            const val = (u.searchParams.get(param) || '').trim();
            if (val) {
                this.#selected.set(opt.key, val);
                changed = true;
            }
        });

        if (!changed && !u.searchParams.get(URL_PARAM_CHILD)) return;

        this.#syncActiveUI();
        this.#updateAvailability();
        this.#syncLineItemProperties();
        this.#setReadyState({ revealWarnings: false });
        this.#emitChange('url');

        const childUrl = this.#getSelectedChildUrl();

        if (childUrl) {
            this.#applyChild(childUrl, { push: false, replace });
        } else {
            this.#writeUrlParams({ push: false, replace: true });

            // Was: #disablePurchaseButtons(isComplete) — inverted. An INCOMPLETE
            // selection enabled the buttons, and this runs at page load from
            // connectedCallback, so it could unlock a locked parent before the
            // guard had even booted.
            this.#refreshPurchaseState();
            this.#resetSpecificationsToBase();
            this.#resetInstructionsToBase();

            this.#resetVisibleProductTitles();
            setFamilyDocumentTitle(this.#baseTitle);
        }
    }

    #mkKey(vals) {
        return vals.map((v) => (v || '').trim()).join('\u001F');
    }

    #rows({ availableOnly = false } = {}) {
        if (!this.#matrix || this.#matrix.size === 0) return [];

        return Array.from(this.#matrix.values())
            .filter((row) => !availableOnly || row.available)
            .map((row) => ({
                ...row,
                keyArr: Array.isArray(row.keyArr) ? row.keyArr.map((v) => (v || '').trim()) : [],
            }));
    }

    #matchesPartial(row, partial) {
        const rowVals = Array.isArray(row?.keyArr) ? row.keyArr : [];

        for (let i = 0; i < rowVals.length; i++) {
            const sel = (partial[i] || '').trim();
            if (!sel) continue;
            if ((rowVals[i] || '').trim() !== sel) return false;
        }

        return true;
    }

    #hasMatchingCombination(partialValues, { availableOnly = false } = {}) {
        const rows = this.#rows({ availableOnly });
        return rows.some((row) => this.#matchesPartial(row, partialValues));
    }

    #onClick(e) {
        const target = e.target instanceof Element ? e.target : null;
        if (!target) return;

        const tipBtn = target.closest('[data-picker-tooltip-btn]');
        if (tipBtn) {
            e.preventDefault();
            e.stopPropagation();
            this.#toggleTooltip(tipBtn);
            return;
        }

        const btn = target.closest('button[data-opt-btn]');
        if (!(btn instanceof HTMLButtonElement)) return;

        const optKey = (btn.getAttribute('data-opt-key') || '').trim();
        const optVal = (btn.getAttribute('data-opt-val') || '').trim();
        if (!optKey) return;

        const state = btn.dataset.state || 'neutral';

        if (state === 'unavailable') {
            this.#showUnavailableMessage(optKey);
            return;
        }

        this.#clearUnavailableMessages();

        const idx = this.#options.findIndex((opt) => opt.key === optKey);
        if (idx === -1) return;

        const cur = (this.#selected.get(optKey) || '').trim();
        const next = cur === optVal ? '' : optVal;

        if (next) {
            this.#selected.set(optKey, next);
        } else {
            this.#selected.delete(optKey);
        }

        this.#pruneInvalidSelections(optKey);

        // NO AUTOFILL. Choosing a pegboard used to silently fill in Style from the
        // first matching row, so a buyer who picked only "SKADIS Pegboard" was given
        // "Angled" without ever seeing it, and the page then resolved and sold that
        // combination. Customers must choose every option themselves — the same class
        // of problem as order JH-2048-2, just reached a different way.
        //
        // #findFirstMatchingRowForAutofill and #isPegboardOption were removed with it;
        // they had no other callers.

        this.#syncActiveUI();
        this.#updateAvailability();
        this.#syncLineItemProperties();
        this.#setReadyState({ revealWarnings: false });
        this.#emitChange('click');

        const childUrl = this.#getSelectedChildUrl();

        if (childUrl) {
            this.#applyChild(childUrl, { push: true, replace: false });
        } else {
            this.#writeUrlParams({ push: false, replace: true });

            // Was: enable on incomplete / disable on complete — inverted, and the
            // direct cause of order JH-2048-2. Incomplete now clears the stale
            // variant id and locks, so the properties written above can never be
            // posted against the previous page's variant.
            this.#refreshPurchaseState();

            // The buyer has interacted, so a greyed-out button must say why:
            // reveal which groups still need a choice rather than leaving a dead
            // control with no explanation.
            if (!this.#isSelectionComplete()) {
                this.#setReadyState({ revealWarnings: true });
            }

            this.#resetSpecificationsToBase();
            this.#resetInstructionsToBase();
            this.#resetVisibleProductTitles();
            setFamilyDocumentTitle(this.#baseTitle);
        }
    }

    #resetSelectionsBelow(changedIndex) {
        for (let i = changedIndex + 1; i < this.#options.length; i++) {
            const key = this.#options[i]?.key;
            if (!key) continue;
            this.#selected.delete(key);
        }
    }

    #toggleTooltip(btnEl) {
        const wrap = btnEl.closest('.picker__label-tooltip');
        if (!wrap) return;

        const tip = wrap.querySelector('[data-picker-tooltip]');
        if (!tip) return;

        const expanded = btnEl.getAttribute('aria-expanded') === 'true';
        const next = !expanded;

        btnEl.setAttribute('aria-expanded', next ? 'true' : 'false');
        tip.hidden = !next;
    }

    #installTooltips() {
        if (this.__cpTooltipBound) return;
        this.__cpTooltipBound = true;

        document.addEventListener('click', (e) => {
            const t = e.target instanceof Element ? e.target : null;
            if (!t) return;

            if (t.closest('custom-picker') === this) return;

            this.querySelectorAll('[data-picker-tooltip-btn][aria-expanded="true"]').forEach((btn) => {
                btn.setAttribute('aria-expanded', 'false');
            });
            this.querySelectorAll('[data-picker-tooltip]:not([hidden])').forEach((tip) => {
                tip.hidden = true;
            });
        });
    }

    #syncLineItemProperties() {
        const root = this.#getRoot();
        const form = root.querySelector('form[action^="/cart/add"]');
        if (!form) return;

        const groups = Array.from(this.querySelectorAll('[data-custom-option][data-opt-key]'));

        const setProp = (label, value) => {
            const l = (label || '').trim();
            if (!l) return;

            const name = `properties[${l}]`;
            let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);

            const v = (value || '').trim();
            if (!v) {
                input?.remove();
                return;
            }

            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
            }

            input.value = v;
        };

        groups.forEach((group) => {
            const optKey = (group.getAttribute('data-opt-key') || '').trim();
            const label = (group.getAttribute('data-line-item-label') || '').trim();
            const val = (this.#selected.get(optKey) || '').trim();
            setProp(label, val);
        });
    }

    #getSelectedChildUrl() {
        if (!this.#matrix || this.#matrix.size === 0) return '';

        const values = this.#options.map((opt) => (this.#selected.get(opt.key) || '').trim());
        if (values.some((v) => !v)) return '';

        const key = this.#mkKey(values);
        const row = this.#matrix.get(key);

        if (!row || !row.available) return '';
        return row.url || '';
    }

    #clearActiveVariantId() {
        const root = this.#getRoot();
        const form = root.querySelector('form[action^="/cart/add"]');
        if (!form) return;

        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = '';
    }

    #ensureValidSelection(source = '') {
        if (!this.#matrix || this.#matrix.size === 0) return;

        const values = this.#options.map((opt) => (this.#selected.get(opt.key) || '').trim());
        const complete = values.every(Boolean);

        if (!complete) return;

        const keyString = this.#mkKey(values);
        const row = this.#matrix.get(keyString);

        if (!row || !row.available) {
            const lastOpt = this.#options[this.#options.length - 1];
            if (lastOpt) this.#selected.delete(lastOpt.key);

            console.warn('[CustomPicker] invalid combination, clearing last option', { source, keyString });
        }
    }

    #syncActiveUI() {
        const buttons = Array.from(this.querySelectorAll('button[data-opt-btn][data-opt-key][data-opt-val]'));

        buttons.forEach((btn) => {
            const optKey = (btn.getAttribute('data-opt-key') || '').trim();
            const optVal = (btn.getAttribute('data-opt-val') || '').trim();
            const activeVal = (this.#selected.get(optKey) || '').trim();
            const isActive = !!activeVal && activeVal === optVal;

            btn.classList.toggle('is-active', isActive);
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
    }

    #isAlwaysAvailableOption(optKey) {
        const opt = this.#options.find((item) => item.key === optKey);
        return !!opt?.always_available;
    }

    #clearUnavailableMessages() {
        this.querySelectorAll('[data-unavailable-message]').forEach((el) => {
            el.hidden = true;
        });
    }

    #showUnavailableMessage(optKey) {
        this.#clearUnavailableMessages();

        const group = this.querySelector(`[data-custom-option][data-opt-key="${CSS.escape(optKey)}"]`);
        if (!group) return;

        const msg = group.querySelector('[data-unavailable-message]');
        if (msg) {
            msg.hidden = false;
        }
    }

    #updateAvailability() {
        const buttons = Array.from(this.querySelectorAll('button[data-opt-btn][data-opt-key][data-opt-val]'));

        if (!this.#matrix || this.#matrix.size === 0) {
            buttons.forEach((btn) => {
                this.#setButtonState(btn, 'neutral');
                btn.hidden = false;
            });
            return;
        }

        const hasAnySelection = this.#hasAnySelection();

        buttons.forEach((btn) => {
            if (!(btn instanceof HTMLButtonElement)) return;

            const optKey = (btn.getAttribute('data-opt-key') || '').trim();
            const optVal = (btn.getAttribute('data-opt-val') || '').trim();

            if (!optKey) {
                this.#setButtonState(btn, 'neutral');
                btn.hidden = false;
                return;
            }

            const activeVal = (this.#selected.get(optKey) || '').trim();
            const isSelected = !!activeVal && activeVal === optVal;
            const isAlwaysAvailable = this.#isAlwaysAvailableOption(optKey);

            if (!hasAnySelection) {
                this.#setButtonState(btn, isSelected ? 'selected' : 'neutral');
                btn.hidden = false;
                return;
            }

            if (isSelected) {
                this.#setButtonState(btn, 'selected');
                btn.hidden = false;
                return;
            }

            if (isAlwaysAvailable) {
                this.#setButtonState(btn, 'available');
                btn.hidden = false;
                return;
            }

            const exists = this.#canValueExistWithCurrentSelection(optKey, optVal);

            this.#setButtonState(btn, exists ? 'available' : 'unavailable');
            btn.hidden = !exists;
        });
    }

    #hasAnySelection() {
        return this.#options.some((opt) => {
            const v = (this.#selected.get(opt.key) || '').trim();
            return !!v;
        });
    }

    #canValueExistWithCurrentSelection(optKey, optVal) {
        const idx = this.#options.findIndex((opt) => opt.key === optKey);
        if (idx === -1) return true;

        const simulated = this.#options.map((opt) => {
            if (opt.key === optKey) return (optVal || '').trim();
            return (this.#selected.get(opt.key) || '').trim();
        });

        return this.#hasMatchingCombination(simulated, { availableOnly: true });
    }

    #pruneInvalidSelections(changedOptKey = '') {
        let changed = true;

        while (changed) {
            changed = false;

            for (const opt of this.#options) {
                const optKey = opt.key;
                const selectedVal = (this.#selected.get(optKey) || '').trim();

                if (!selectedVal) continue;

                const simulated = this.#options.map((item) => {
                    if (item.key === optKey) return selectedVal;
                    return (this.#selected.get(item.key) || '').trim();
                });

                const exists = this.#hasMatchingCombination(simulated, { availableOnly: true });

                if (!exists) {
                    if (optKey === changedOptKey) continue;

                    this.#selected.delete(optKey);
                    changed = true;
                }
            }
        }

        if (changedOptKey) {
            const selectedVal = (this.#selected.get(changedOptKey) || '').trim();

            if (selectedVal) {
                const simulated = this.#options.map((opt) => {
                    if (opt.key === changedOptKey) return selectedVal;
                    return (this.#selected.get(opt.key) || '').trim();
                });

                const exists = this.#hasMatchingCombination(simulated, { availableOnly: true });

                if (!exists) {
                    this.#options.forEach((opt) => {
                        if (opt.key !== changedOptKey) {
                            this.#selected.delete(opt.key);
                        }
                    });
                }
            }
        }
    }

    #setButtonState(btn, state) {
        if (!(btn instanceof HTMLButtonElement)) return;

        btn.dataset.state = state;
        btn.classList.toggle('is-active', state === 'selected');
        btn.classList.toggle('is-disabled', false);

        btn.removeAttribute('disabled');
        btn.hidden = false;

        btn.setAttribute('aria-disabled', state === 'unavailable' ? 'true' : 'false');
        btn.setAttribute('aria-pressed', state === 'selected' ? 'true' : 'false');

        if (state === 'unavailable') {
            btn.setAttribute('data-unavailable-combination', '');
        } else {
            btn.removeAttribute('data-unavailable-combination');
        }
    }

    // Controls that can explain themselves. When the selection is merely incomplete
    // these are SOFT-locked: styled inactive and marked aria-disabled, but still
    // clickable so the click can surface "Please select all options first". A truly
    // disabled button swallows the click and tells the buyer nothing.
    //
    // Everything else (Buy it now, accelerated checkout) is always hard-disabled —
    // those are third-party controls we cannot annotate.
    #EXPLAINABLE_SELECTORS = [
        'button[name="add"]',
        'add-to-cart-component button',
        '.product-form__submit',
        'sticky-add-to-cart button.add-to-cart-button',
    ];

    #isExplainable(el) {
        return this.#EXPLAINABLE_SELECTORS.some((sel) => el.matches?.(sel));
    }

    // A soft lock is only safe while custom-options-guard.js is live to veto the
    // click it deliberately allows through. If the guard died, an unfinished
    // selection must stay genuinely disabled rather than merely looking that way.
    #canSoftLock() {
        return document.documentElement.dataset.familyGuardEnforcing === '1';
    }

    #disablePurchaseButtons(disabled, { soft = false } = {}) {
        const root = this.#getRoot();

        const selectors = [
            'button[name="add"]',
            'add-to-cart-component button',
            'product-form-component button[type="submit"]',
            '.product-form__submit',
            '.shopify-payment-button__button',
            'shopify-accelerated-checkout',
            'shopify-buy-it-now-button',
            // The sticky bar's button lives outside add-to-cart-component and has
            // no name="add", so none of the selectors above ever matched it. It
            // stayed live while every other purchase control was locked.
            'sticky-add-to-cart button.add-to-cart-button',
        ];

        const nodes = [];
        selectors.forEach((sel) => root.querySelectorAll(sel).forEach((el) => nodes.push(el)));

        nodes.forEach((el) => {
            if (!(el instanceof Element)) return;

            // The server-side family-parent lock outranks every caller. The marker
            // means "locked" unless the guard has explicitly cleared it to "0" by
            // certifying it can enforce selection client-side. Liquid ships it with
            // no value, so absence of "0" — not presence of "1" — is the test.
            const marker = el.getAttribute('data-family-parent-locked');
            const serverLocked = marker !== null && marker !== '0';
            const next = !!disabled || serverLocked;

            // Record the picker's OWN intent separately, so the guard can lift the
            // family lock without stomping a disable the picker imposed for an
            // incomplete selection.
            // Record BOTH the picker's intent and its softness. The guard reads this
            // when it lifts the family lock: without the "soft" marker it would
            // re-apply a hard disable and the button could never explain itself.
            if (disabled) el.setAttribute('data-picker-locked', soft ? 'soft' : '1');
            else el.removeAttribute('data-picker-locked');

            // Soft lock: look inactive, stay clickable. Only ever applied when the
            // server-side family lock is NOT set — a server-locked control must stay
            // genuinely disabled so a broken-JS browser can never purchase.
            const softLock =
                next && soft && !serverLocked && this.#canSoftLock() && this.#isExplainable(el);
            el.classList.toggle('is-soft-locked', softLock);

            // Must be the string "true" — toggleAttribute would set it to "" , which
            // is not a valid aria-disabled value and fails an === 'true' test.
            if (next) el.setAttribute('aria-disabled', 'true');
            else el.removeAttribute('aria-disabled');

            if (el instanceof HTMLButtonElement) {
                el.disabled = softLock ? false : next;
            } else {
                if (next && !softLock) el.setAttribute('disabled', '');
                else el.removeAttribute('disabled');
            }
        });
    }

    // True when the current selection resolves to the product page we are already
    // on. Compares pathname only: the variant/state query params legitimately
    // differ from what #buildChildUrlWithState would produce.
    #isOnSelectedChildPage() {
        const childUrl = this.#getSelectedChildUrl();
        if (!childUrl) return false;

        try {
            return new URL(childUrl, window.location.origin).pathname === window.location.pathname;
        } catch (e) {
            return false;
        }
    }

    // Fail-closed recomputation of purchase state from current truth. Called
    // wherever a selection changes and wherever #applyChild stops without
    // navigating, so no code path can leave the buttons in a stale state.
    //
    // An INCOMPLETE selection must never be purchasable: #syncLineItemProperties
    // has already rewritten the line-item properties to match the new picker
    // state, while the form still holds the variant of the page we are standing
    // on. Buying in that window produces a line item whose variant contradicts
    // its own properties — order JH-2048-2 (Angled variant, "Style: Vertical").
    #refreshPurchaseState() {
        if (!this.#isSelectionComplete()) {
            this.#clearActiveVariantId();

            // Soft: the buyer simply has not finished choosing, which is the normal
            // state on every family-parent page now that nothing is auto-selected.
            // They must be able to click and be told what is missing. The variant id
            // is cleared regardless, and the guard vetoes the submit.
            this.#disablePurchaseButtons(true, { soft: true });
            return;
        }

        // Complete but unresolved (invalid combination), or resolved to a page we
        // have not navigated to yet — both mean the form cannot be trusted.
        if (!this.#isOnSelectedChildPage()) {
            this.#clearActiveVariantId();
            this.#disablePurchaseButtons(true);
            return;
        }

        // Reachable after an earlier incomplete state cleared the id and the
        // product fetch then failed to resolve a variant. Enabling here would hand
        // the buyer a live button with nothing to post.
        const root = this.#getRoot();
        const variantId = root.querySelector('form[action^="/cart/add"] input[name="id"]')?.value;
        if (!(variantId || '').trim()) {
            this.#disablePurchaseButtons(true);
            return;
        }

        // Never resurrect a sold-out control. When the product fetch fails we have
        // no variant data of our own, so defer to what the server rendered for the
        // variant this page is showing.
        const serverAvailability = root
            .querySelector('sticky-add-to-cart')
            ?.getAttribute('data-variant-available');

        if (serverAvailability === 'false') {
            this.#disablePurchaseButtons(true);
            return;
        }

        this.#disablePurchaseButtons(false);
    }

    #setDisabled(btn, disabled) {
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.disabled = !!disabled;
        btn.classList.toggle('is-disabled', !!disabled);
        btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    #isSelectionComplete() {
        return this.#options.every((opt) => {
            const v = (this.#selected.get(opt.key) || '').trim();
            return !!v;
        });
    }

    #setReadyState({ revealWarnings }) {
        const allSelected = this.#options.every((opt) => {
            const v = (this.#selected.get(opt.key) || '').trim();
            return !!v;
        });

        this.dataset.ready = allSelected ? 'true' : 'false';

        const groups = Array.from(this.querySelectorAll('[data-custom-option][data-opt-key]'));
        groups.forEach((group) => {
            const optKey = (group.getAttribute('data-opt-key') || '').trim();
            const selected = !!(this.#selected.get(optKey) || '').trim();

            const warning = group.querySelector('[data-warning-opt]');
            if (!warning) return;

            warning.hidden = !revealWarnings || selected;
        });
    }

    #getSectionElement() {
        return this.closest('.shopify-section');
    }

    #getSectionId() {
        const sectionEl = this.#getSectionElement();
        const rawId = sectionEl?.id || '';
        return rawId.replace(/^shopify-section-/, '').trim();
    }

    async #fetchSectionHtml(productUrl, signal, variant = null) {
        const sectionId = this.#getSectionId();
        if (!sectionId) {
            console.warn('[CustomPicker] section id not found');
            return null;
        }

        const u = new URL(productUrl, window.location.origin);

        const currentUrl = new URL(window.location.href);
        const view = currentUrl.searchParams.get('view');
        if (view && !u.searchParams.has('view')) {
            u.searchParams.set('view', view);
        }

        if (variant?.id) {
            u.searchParams.set('variant', String(variant.id));
        }

        u.searchParams.set('section_id', sectionId);

        const res = await fetch(u.toString(), {
            signal,
            headers: { Accept: 'text/html' },
        });

        if (!res.ok) {
            throw new Error(`Failed fetch section html ${u} (${res.status})`);
        }

        return await res.text();
    }

    #replaceNode(fromRoot, toRoot, selector) {
        const current = fromRoot.querySelector(selector);
        const next = toRoot.querySelector(selector);

        if (!current || !next) return false;

        current.replaceWith(next.cloneNode(true));
        return true;
    }

    #syncVisibleProductTitles(product, variant, childUrl) {
        const root = this.#getRoot();
        if (!root || !product) return;

        const productTitle = String(product.title || '').trim();
        const variantTitle = String(variant?.title || '').trim();
        const productLink = String(childUrl || product.url || '').trim();

        if (productTitle) {
            root.querySelectorAll('.view-product-title a').forEach((el) => {
                el.textContent = productTitle;
                if (productLink) el.href = productLink;
            });

            root.querySelectorAll('a[ref="productTitleLink"]').forEach((el) => {
                const textNode = el.querySelector('p');
                if (textNode) {
                    textNode.textContent = productTitle;
                } else {
                    el.textContent = productTitle;
                }

                if (productLink) el.href = productLink;
            });

            root.querySelectorAll('[data-testid="sticky-product-title"]').forEach((el) => {
                el.textContent = productTitle;
            });

            root.querySelectorAll('.product-info-header .text-block h1').forEach((el) => {
                el.textContent = productTitle;
            });
        }

        root.querySelectorAll('[data-testid="sticky-variant-title"]').forEach((el) => {
            el.textContent = variantTitle;
        });
    }

    #replaceNativeProductBlocks(nextSectionRoot) {
        const root = this.#getRoot();
        if (!root || !nextSectionRoot) return;

        const selectors = [
            'variant-picker',
            'product-form-component',
            'add-to-cart-component',
            'product-price',
            'sticky-add-to-cart',
            '.view-product-title',
            'a[ref="productTitleLink"]',
            '[data-testid="sticky-product-title"]',
            '[data-testid="sticky-variant-title"]',
            '.product-info-header .text-block h1',
        ];

        selectors.forEach((selector) => {
            this.#replaceNode(root, nextSectionRoot, selector);
        });
    }

    #findOptionInputsForValue(root, value) {
        if (!root || !value) return [];

        const norm = (s) => String(s || '').trim().toLowerCase();

        return Array.from(
            root.querySelectorAll('variant-picker input[type="radio"], variant-picker option')
        ).filter((el) => norm(el.value) === norm(value));
    }

    #syncNativePickerSelectionFromVariant(variant) {
        const root = this.#getRoot();
        if (!root || !variant) return;

        const optionValues = [variant.option1, variant.option2, variant.option3]
            .map((v) => String(v || '').trim())
            .filter(Boolean);

        optionValues.forEach((value) => {
            const matched = this.#findOptionInputsForValue(root, value);

            matched.forEach((el) => {
                if (el instanceof HTMLOptionElement) {
                    el.selected = true;
                    const select = el.closest('select');
                    if (select) {
                        select.value = el.value;
                    }
                    return;
                }

                if (el instanceof HTMLInputElement && el.type === 'radio') {
                    el.checked = true;
                    el.setAttribute('checked', '');
                    el.setAttribute('data-current-checked', 'true');

                    if (el.closest('[data-family-color-picker]')) {
                        this.#setColorSwatchValue(el.value);
                    }

                    const groupName = el.name;
                    if (groupName) {
                        root
                            .querySelectorAll(`variant-picker input[type="radio"][name="${CSS.escape(groupName)}"]`)
                            .forEach((input) => {
                                if (input !== el) {
                                    input.checked = false;
                                    input.removeAttribute('checked');
                                    input.setAttribute('data-current-checked', 'false');
                                }
                            });
                    }
                }
            });
        });

        const variantInput = root.querySelector('form[action^="/cart/add"] input[name="id"]');
        if (variantInput) {
            variantInput.value = String(variant.id);
        }
    }

    #dispatchNativeVariantInputs() {
        const root = this.#getRoot();
        if (!root) return;

        const changedInputs = root.querySelectorAll(
            'variant-picker input[type="radio"]:checked, variant-picker select'
        );

        changedInputs.forEach((el) => {
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    #getStoredPreferredColor() {
        const root = this.#getRoot();
        return (root?.__customPickerPreferredColor || '').trim();
    }

    #setStoredPreferredColor(value) {
        const root = this.#getRoot();
        if (!root) return;
        root.__customPickerPreferredColor = String(value || '').trim();
    }

    #captureCurrentColorPreference() {
        const color = this.#getSelectedColorValue();
        if (color) {
            this.#setStoredPreferredColor(color);
        }
        return color || '';
    }

    #findNativeColorFieldset() {
        const root = this.#getRoot();
        return root?.querySelector('[data-family-color-picker]') || null;
    }

    #setColorSwatchValue(value, root = this.#getRoot()) {
        const colorValue = String(value || '').trim();
        if (!root || !colorValue) return;

        const fieldset = root.querySelector('[data-family-color-picker]');
        const swatchValue = fieldset?.querySelector('.variant-option__swatch-value');

        if (swatchValue) {
            swatchValue.textContent = colorValue;
        }
    }

    #syncIncomingColorPickerSelection(root, colorValue, variant) {
        const fieldset = root?.querySelector('[data-family-color-picker]');
        if (!fieldset) return false;

        const norm = (s) => String(s || '').trim().toLowerCase();
        const values = [colorValue, variant?.option1, variant?.option2, variant?.option3, variant?.title]
            .map((value) => String(value || '').trim())
            .filter(Boolean);

        if (!values.length) return false;

        const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        const input = inputs.find((item) => values.some((value) => norm(item.value) === norm(value)));

        if (!input) return false;

        inputs.forEach((item) => {
            item.checked = false;
            item.removeAttribute('checked');
            item.setAttribute('data-current-checked', 'false');
        });

        input.checked = true;
        input.setAttribute('checked', '');
        input.setAttribute('data-current-checked', 'true');
        input.setAttribute('data-previous-checked', 'false');

        this.#setColorSwatchValue(input.value, root);

        return true;
    }

    #findColorInputByValue(value) {
        const fieldset = this.#findNativeColorFieldset();
        if (!fieldset || !value) return null;

        const norm = (s) => String(s || '').trim().toLowerCase();

        return Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((input) => {
            return norm(input.value) === norm(value);
        }) || null;
    }

    #applyPreferredColorSelection(colorValue) {
        const root = this.#getRoot();
        if (!root || !colorValue) return false;

        const input = this.#findColorInputByValue(colorValue);
        if (!input) return false;

        const groupName = input.name;

        if (groupName) {
            root
                .querySelectorAll(`variant-picker input[type="radio"][name="${CSS.escape(groupName)}"]`)
                .forEach((el) => {
                    el.checked = false;
                    el.removeAttribute('checked');
                    el.setAttribute('data-current-checked', 'false');
                });
        }

        input.checked = true;
        input.setAttribute('checked', '');
        input.setAttribute('data-current-checked', 'true');
        this.#setColorSwatchValue(input.value);

        return true;
    }

    async #patchNativeUiFromChild(childUrl, variant) {
        const preservedQuantity = this.#getCurrentQuantity();
        const preservedColor = this.#captureCurrentColorPreference() || this.#getStoredPreferredColor();

        const html = await this.#fetchSectionHtml(childUrl, this.#abortController.signal, variant);
        if (!html) return;

        const tpl = document.createElement('template');
        tpl.innerHTML = html.trim();

        const incomingFieldset = tpl.content.querySelector('[data-family-color-picker]');
        const incomingLegend = incomingFieldset
            ?.querySelector('.picker__label > span:first-child')
            ?.textContent?.trim();

        const incomingMessage = !!tpl.content.querySelector('[data-warning-color]');

        const nextSectionRoot =
            tpl.content.querySelector('.shopify-section') ||
            tpl.content.querySelector('[data-family-guard-scope]') ||
            tpl.content;

        this.#syncIncomingColorPickerSelection(nextSectionRoot, preservedColor, variant);
        this.#replaceNativeProductBlocks(nextSectionRoot);
        this.#syncLineItemProperties();

        const restoredPreferredColor = preservedColor
            ? this.#applyPreferredColorSelection(preservedColor)
            : false;

        if (!restoredPreferredColor) {
            this.#syncNativePickerSelectionFromVariant(variant);
        }

        this.#setActiveVariantId(variant.id);
        this.#restoreQuantity(preservedQuantity);
        this.#normalizeColorLabel();
        this.#ensureColorInlineMessageVisible();

        requestAnimationFrame(() => {
            this.#dispatchNativeVariantInputs();

            requestAnimationFrame(() => {
                if (preservedColor) {
                    this.#applyPreferredColorSelection(preservedColor);
                }

                this.#restoreQuantity(preservedQuantity);
                this.#normalizeColorLabel();
                this.#ensureColorInlineMessageVisible();
                this.#syncLineItemProperties();

                const currentLegend = this.#getRoot()
                    ?.querySelector('[data-family-color-picker] .picker__label > span:first-child')
                    ?.textContent?.trim();

                const currentMessageVisible = !this.#getRoot()
                    ?.querySelector('[data-warning-color]')
                    ?.hidden;
            });
        });
    }

    #ensureColorInlineMessageVisible() {
        const root = this.#getRoot();
        if (!root) return;

        const fieldset = root.querySelector('[data-family-color-picker]');
        if (!fieldset) return;

        let message = root.querySelector('[data-warning-color]');
        if (!message) return;

        const nextSibling = fieldset.nextElementSibling;
        if (nextSibling !== message) {
            fieldset.insertAdjacentElement('afterend', message);
        }

        message.hidden = false;
        message.removeAttribute('hidden');
        message.style.display = '';
    }

    #getCurrentQuantity() {
        const root = this.#getRoot();
        const input = root?.querySelector('input[name="quantity"]');
        const value = parseInt(input?.value || '1', 10);
        return Number.isFinite(value) && value > 0 ? value : 1;
    }

    #restoreQuantity(quantity) {
        const root = this.#getRoot();
        if (!root) return;

        const normalized = Number.isFinite(Number(quantity)) && Number(quantity) > 0
            ? Number(quantity)
            : 1;

        const inputs = root.querySelectorAll('input[name="quantity"]');
        if (!inputs.length) return;

        inputs.forEach((input) => {
            input.value = String(normalized);
            input.setAttribute('value', String(normalized));

            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        root.dispatchEvent(
            new CustomEvent('quantity-selector:update', {
                bubbles: true,
                detail: { quantity: normalized },
            })
        );
    }

    #emitChange(source = '') {
        const detail = {};
        this.#options.forEach((opt) => {
            detail[opt.key] = (this.#selected.get(opt.key) || '').trim();
        });

        this.dispatchEvent(
            new CustomEvent('custom-picker:change', {
                bubbles: true,
                detail: {
                    selected: detail,
                    ready: this.dataset.ready === 'true',
                    source,
                },
            })
        );
    }

    #writeUrlParams({ push, replace }) {
        const u = new URL(window.location.href);

        this.#options.forEach((opt) => {
            const param = URL_PREFIX + String(opt.mf_key || '').trim();
            if (!param || param === URL_PREFIX) return;

            const v = (this.#selected.get(opt.key) || '').trim();
            if (v) u.searchParams.set(param, v);
            else u.searchParams.delete(param);
        });

        u.searchParams.delete(URL_PARAM_CHILD);

        const view = getViewParameterValue();
        if (view) u.searchParams.set('view', view);

        const next = u.pathname + (u.search ? u.search : '') + (u.hash || '');

        if (replace) window.history.replaceState({ customPicker: true }, '', next);
        else if (push) window.history.pushState({ customPicker: true }, '', next);
    }

    #redirectLegacyFpChildUrl() {
        const u = new URL(window.location.href);
        const childHandle = (u.searchParams.get(URL_PARAM_CHILD) || '').trim();

        if (!childHandle) return false;

        if (!/^[a-z0-9][a-z0-9-]*$/i.test(childHandle)) {
            u.searchParams.delete(URL_PARAM_CHILD);
            window.history.replaceState({ customPicker: true }, '', u.pathname + (u.search || '') + (u.hash || ''));
            return false;
        }

        const currentHandle = this.#getHandleFromProductUrl(u.pathname);

        u.searchParams.delete(URL_PARAM_CHILD);

        if (currentHandle !== childHandle) {
            u.pathname = `/products/${childHandle}`;
        }

        const next = u.pathname + (u.search || '') + (u.hash || '');
        const current = window.location.pathname + window.location.search + window.location.hash;

        if (next !== current) {
            this.#isNavigatingToChild = true;
            window.location.replace(next);
            return true;
        }

        return false;
    }

    #buildChildUrlWithState(childUrl, variantId = '') {
        const u = new URL(childUrl, window.location.origin);

        this.#options.forEach((opt) => {
            const param = URL_PREFIX + String(opt.mf_key || '').trim();
            if (!param || param === URL_PREFIX) return;

            const value = (this.#selected.get(opt.key) || '').trim();
            if (value) u.searchParams.set(param, value);
            else u.searchParams.delete(param);
        });

        u.searchParams.delete(URL_PARAM_CHILD);

        const view = getViewParameterValue();
        if (view) u.searchParams.set('view', view);

        if (variantId) {
            u.searchParams.set('variant', String(variantId));
        } else {
            u.searchParams.delete('variant');
        }

        return u.pathname + (u.search || '') + (u.hash || '');
    }

    #getComparableLocation(url) {
        const u = new URL(url, window.location.origin);
        const entries = Array.from(u.searchParams.entries()).sort(([aKey, aVal], [bKey, bVal]) => {
            if (aKey === bKey) return String(aVal).localeCompare(String(bVal));
            return String(aKey).localeCompare(String(bKey));
        });

        const query = new URLSearchParams(entries).toString();
        return u.pathname + (query ? `?${query}` : '') + (u.hash || '');
    }

    #isCurrentLocation(url) {
        const current = window.location.pathname + window.location.search + window.location.hash;
        return this.#getComparableLocation(current) === this.#getComparableLocation(url);
    }

    #normalizeColorLabel() {
        const root = this.#getRoot();
        if (!root) return;

        const fieldset = root.querySelector('[data-family-color-picker]');
        if (!fieldset) return;

        const legend = fieldset.querySelector('.picker__label > span:first-child');
        if (!legend) return;

        const configuredLabel = (
            legend.dataset.colorPickerLabel ||
            legend.textContent ||
            'Select Color:'
        ).trim();

        legend.textContent = configuredLabel.endsWith(':')
            ? configuredLabel
            : `${configuredLabel}:`;
    }

    async #applyChild(childUrl, { push = true, replace = false } = {}) {
        if (this.#isNavigatingToChild) return;

        // Lock purchasing the instant a complete selection resolves to a child:
        // between here and the navigation there is a window where the form still
        // holds the PARENT variant, and a fast tap on Add to cart would buy the
        // ambiguous parent (seen in real orders). The child page re-enables.
        this.#disablePurchaseButtons(true);

        // Set once a branch has already put the buttons in their correct final
        // state (e.g. honouring a sold-out variant), so the safety net in the
        // finally block does not overwrite a more informed decision.
        let settled = false;

        try {
            this.#abortController?.abort();
            this.#abortController = new AbortController();

            this.dataset.loading = 'true';

            const currentColor = this.#captureCurrentColorPreference();
            const preferredColor = currentColor || this.#getStoredPreferredColor();

            const product = await this.#fetchProductJson(childUrl, this.#abortController.signal);
            const variant = product ? this.#pickVariant(product, { color: preferredColor }) : null;

            const targetUrl = this.#buildChildUrlWithState(childUrl, variant?.id || '');

            if (this.#isCurrentLocation(targetUrl)) {
                this.#syncLineItemProperties();

                if (variant) {
                    this.#setActiveVariantId(variant.id);
                    this.#syncAvailabilityToButtons(variant);
                    this.#disablePurchaseButtons(!variant?.available);
                    // Availability is more specific than #refreshPurchaseState can
                    // infer — keep it, and stop the finally block clobbering it.
                    settled = true;
                }

                // No variant means the product JSON was empty or rate-limited. We
                // are on the right page, so the finally block recomputes from the
                // server-rendered form rather than leaving the button dead.
                return;
            }

            this.#isNavigatingToChild = true;

            if (replace) {
                window.location.replace(targetUrl);
            } else {
                window.location.assign(targetUrl);
            }
        } catch (e) {
            // An abort means a newer #applyChild superseded this one; that call
            // owns the button state from here. `return` inside catch STILL runs the
            // finally block, so mark this call settled — otherwise the superseded
            // invocation re-enables buttons the newer one deliberately locked.
            if (e?.name === 'AbortError') {
                settled = true;
                return;
            }

            console.error('[CustomPicker] child redirect failed', e);

            const fallbackUrl = this.#buildChildUrlWithState(childUrl);

            if (!this.#isCurrentLocation(fallbackUrl)) {
                this.#isNavigatingToChild = true;

                if (replace) {
                    window.location.replace(fallbackUrl);
                } else if (push) {
                    window.location.assign(fallbackUrl);
                }
            }
        } finally {
            // Every path that stops WITHOUT navigating must hand the buttons back
            // in a correct state. Previously the pre-fetch lock at the top of this
            // method was permanent on failure, rate-limit, and no-op-navigation
            // paths — the button stayed dead until a hard reload.
            if (!this.#isNavigatingToChild) {
                this.dataset.loading = 'false';
                if (!settled) this.#refreshPurchaseState();
            }
        }
    }

    #resetVisibleProductTitles() {
        const root = this.#getRoot();
        if (!root) return;

        const title = this.#baseTitle || '';
        if (!title) return;

        root.querySelectorAll('.view-product-title a').forEach((el) => {
            el.textContent = title;
            if (this.#mainUrl) el.href = this.#mainUrl;
        });

        root.querySelectorAll('a[ref="productTitleLink"]').forEach((el) => {
            const textNode = el.querySelector('p');
            if (textNode) {
                textNode.textContent = title;
            } else {
                el.textContent = title;
            }

            if (this.#mainUrl) el.href = this.#mainUrl;
        });

        root.querySelectorAll('[data-testid="sticky-product-title"]').forEach((el) => {
            el.textContent = title;
        });

        root.querySelectorAll('.product-info-header .text-block h1').forEach((el) => {
            el.textContent = title;
        });
    }

    #buildPricingPayload(product) {
        const variants = Array.isArray(product?.variants) ? product.variants : [];

        const payload = {
            variants: {},
        };

        variants.forEach((variant) => {
            payload.variants[String(variant.id)] = {
                price: Number(variant.price || 0),
                compare_at_price: Number(variant.compare_at_price || 0),
                price_breaks: Array.isArray(variant.quantity_price_breaks)
                    ? variant.quantity_price_breaks.map((item) => ({
                        minimum_quantity: Number(item.minimum_quantity || 0),
                        price: Number(item.price || 0),
                    }))
                    : [],
            };
        });

        return payload;
    }

    #syncThemeProductContext(product, variant, childUrl) {
        const root = this.#getRoot();
        if (!root || !product) return;

        const productId = String(product.id || '');
        const productUrl = String(childUrl || product.url || '');

        if (!productId || !productUrl) return;

        root.__customPickerProductId = productId;
        root.__customPickerProductUrl = productUrl;

        root.querySelectorAll('variant-picker').forEach((picker) => {
            picker.dataset.productId = productId;
            picker.dataset.productUrl = productUrl;
        });

        root.querySelectorAll('product-form-component').forEach((formComponent) => {
            formComponent.dataset.productId = productId;
        });

        root.querySelectorAll('product-price').forEach((priceComponent) => {
            priceComponent.dataset.productId = productId;
        });

        root.querySelectorAll('sticky-add-to-cart').forEach((sticky) => {
            sticky.dataset.productId = productId;
            sticky.dataset.currentVariantId = String(variant?.id || '');
            sticky.dataset.variantAvailable = String(!!variant?.available);
        });
    }

    #syncPricingJson(product) {
        const root = this.#getRoot();
        if (!root || !product) return;

        const payload = this.#buildPricingPayload(product);

        root.__customPickerPricingPayload = payload;
        root.__customPickerChildProductHandle = this.#getHandleFromProductUrl(product.url || '');

        const scripts = root.querySelectorAll('[data-product-pricing-json]');
        if (!scripts.length) {
            console.warn('[CustomPicker] data-product-pricing-json not found');
            return;
        }

        scripts.forEach((script) => {
            script.textContent = JSON.stringify(payload);
        });
    }

    #getHandleFromProductUrl(url) {
        try {
            const u = new URL(url, window.location.origin);
            const m = u.pathname.match(/\/products\/([^\/]+)/);
            return m ? m[1] : '';
        } catch {
            const m = String(url).match(/\/products\/([^\/]+)/);
            return m ? m[1] : '';
        }
    }

    async #fetchProductJson(productUrl, signal) {
        const cacheKey = String(productUrl);
        if (this.#cache.has(cacheKey)) return this.#cache.get(cacheKey);

        try {
            const u = new URL(productUrl, window.location.origin);
            const jsonUrl = `${u.pathname}.js`;
            const res = await fetch(jsonUrl, { signal, headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(`Failed fetch ${jsonUrl} (${res.status})`);
            const data = await res.json();
            this.#cache.set(cacheKey, data);
            return data;
        } catch (e) {
            console.error('[CustomPicker] fetchProductJson failed', e);
            return null;
        }
    }

    #getSelectedColorValue() {
        const root = this.#getRoot();

        const fieldset = root.querySelector('[data-family-color-picker]');
        if (!fieldset) return null;

        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value || null;

        const current = fieldset.querySelector('input[type="radio"][data-current-checked="true"]');
        return current?.value || null;
    }

    #pickVariant(product, { color } = {}) {
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        if (!variants.length) return null;

        const norm = (s) => (s || '').trim().toLowerCase();

        if (color) {
            const c = norm(color);
            const byColor = variants.find((v) =>
                [v.option1, v.option2, v.option3].some((opt) => norm(opt) === c)
            );
            if (byColor) return byColor;
        }

        const available = variants.find((v) => v.available);
        return available || variants[0];
    }

    #setActiveVariantId(variantId) {
        const root = this.#getRoot();
        const form = root.querySelector('form[action^="/cart/add"]');
        if (!form) {
            console.warn('[CustomPicker] product form not found in scope');
            return;
        }

        let idInput = form.querySelector('input[name="id"]');
        if (!idInput) {
            idInput = document.createElement('input');
            idInput.type = 'hidden';
            idInput.name = 'id';
            form.appendChild(idInput);
        }

        idInput.value = String(variantId);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    #renderPriceFromVariant(variant) {
        const root = this.#getRoot();
        if (!root || !variant) return;

        const price = typeof variant.price === 'number' ? variant.price : null;
        const compareAt =
            typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null;

        if (price === null) return;

        const priceText = this.#formatMoney(price);
        const compareText = compareAt ? this.#formatMoney(compareAt) : '';

        const priceEls = Array.from(root.querySelectorAll('[data-product-price]'));
        const compareEls = Array.from(root.querySelectorAll('[data-product-compare-price]'));
        const containers = Array.from(root.querySelectorAll('[data-product-price-container]'));

        if (!priceEls.length) {
            console.warn('[CustomPicker] [data-product-price] not found');
            return;
        }

        priceEls.forEach((el) => {
            el.textContent = priceText;
        });

        compareEls.forEach((el) => {
            if (compareAt && compareAt > price) {
                el.textContent = compareText;
                el.hidden = false;
            } else {
                el.textContent = '';
                el.hidden = true;
            }
        });

        containers.forEach((el) => {
            el.classList.toggle('price--on-sale', !!(compareAt && compareAt > price));
        });
    }

    #formatMoney(cents) {
        try {
            const fmt = window.Shopify?.formatMoney;
            if (typeof fmt === 'function') {
                const moneyFormat = window?.theme?.money_format || window?.Shopify?.money_format;
                return fmt(cents, moneyFormat);
            }
        } catch { }

        const n = cents / 100;
        const cur = window.Shopify?.currency?.active || 'USD';
        return n.toLocaleString(undefined, { style: 'currency', currency: cur });
    }

    #syncAvailabilityToButtons(variant) {
        const available = !!variant?.available;
        const root = this.#getRoot();

        const selectors = [
            'button[name="add"]',
            'add-to-cart-component button',
            'product-form-component button[type="submit"]',
            '.product-form__submit',
            '.shopify-payment-button__button',
            '.shopify-payment-button',
            '[data-shopify="payment-button"]',
            'shopify-accelerated-checkout',
            'shopify-buy-it-now-button',
        ];

        const nodes = [];
        selectors.forEach((sel) => root.querySelectorAll(sel).forEach((el) => nodes.push(el)));

        nodes.forEach((el) => {
            if (!(el instanceof Element)) return;

            el.toggleAttribute('aria-disabled', !available);

            if (el instanceof HTMLButtonElement) {
                el.disabled = !available;
            } else {
                if (!available) el.setAttribute('disabled', '');
                else el.removeAttribute('disabled');
            }
        });
    }

    #getOptionsJson() {
        const el = this.querySelector('script[data-custom-options-json]');
        return el?.textContent || '[]';
    }

    #getMatrixJson() {
        const el = this.querySelector('script[data-custom-matrix-json]');
        return el?.textContent || '[]';
    }

    #getSpecificationsJson() {
        const el = this.querySelector('script[data-custom-specifications-json]');
        return el?.textContent || '{}';
    }

    #parseSpecifications(jsonText) {
        let obj = {};

        try {
            obj = JSON.parse(jsonText);
        } catch (e) {
            console.warn('[CustomPicker] specifications JSON parse failed', e);
            obj = {};
        }

        return obj && typeof obj === 'object' ? obj : {};
    }

    #captureBaseSpecifications() {
        const root = this.#getRoot();
        const container = root?.querySelector('.js-product-specifications');
        if (!container) return;

        this.#baseSpecificationsHtml = container.innerHTML;
        this.#toggleDynamicProductRow(container, this.#baseSpecificationsHtml);
    }

    #resetSpecificationsToBase() {
        const root = this.#getRoot();
        const container = root?.querySelector('.js-product-specifications');
        if (!container) return;

        container.innerHTML = this.#baseSpecificationsHtml || '';
        this.#toggleDynamicProductRow(container, this.#baseSpecificationsHtml);
    }

    #syncSpecificationsFromChild(childUrl) {
        const root = this.#getRoot();
        const container = root?.querySelector('.js-product-specifications');
        if (!container || !childUrl) return;

        const html = this.#specificationsMap?.[childUrl] || '';
        container.innerHTML = html;
        this.#toggleDynamicProductRow(container, html);
    }


    #getInstructionsJson() {
        const el = this.querySelector('script[data-custom-instructions-json]');
        return el?.textContent || '{}';
    }

    #parseInstructions(jsonText) {
        try {
            const obj = JSON.parse(jsonText);
            return obj && typeof obj === 'object' ? obj : {};
        } catch (e) {
            console.warn('[CustomPicker] instructions JSON parse failed', e);
            return {};
        }
    }

    #toggleDynamicProductRow(container, html) {
        const accordion = container.closest('accordion-custom');
        if (!accordion) return;

        const hasContent = String(html || '').replace(/<[^>]*>/g, '').trim() !== '';
        accordion.classList.toggle('hidden', !hasContent);
    }

    #captureBaseInstructions() {
        const root = this.#getRoot();
        const container = root?.querySelector('.js-product-instructions');
        if (!container) return;

        this.#baseInstructionsHtml = container.innerHTML;
        this.#toggleDynamicProductRow(container, this.#baseInstructionsHtml);
    }

    #resetInstructionsToBase() {
        const root = this.#getRoot();
        const container = root?.querySelector('.js-product-instructions');
        if (!container) return;

        container.innerHTML = this.#baseInstructionsHtml || '';
        this.#toggleDynamicProductRow(container, this.#baseInstructionsHtml);
    }

    #syncInstructionsFromChild(childUrl) {
        const root = this.#getRoot();
        const container = root?.querySelector('.js-product-instructions');
        if (!container || !childUrl) return;

        const html = this.#instructionsMap?.[childUrl] || '';
        container.innerHTML = html;
        this.#toggleDynamicProductRow(container, html);
    }

    #parseOptions(jsonText) {
        let arr = [];
        try {
            arr = JSON.parse(jsonText);
        } catch (e) {
            console.warn('[CustomPicker] options JSON parse failed', e);
            arr = [];
        }

        return Array.isArray(arr) ? arr : [];
    }

    #parseMatrix(jsonText) {
        const map = new Map();
        let arr = [];

        try {
            arr = JSON.parse(jsonText);
        } catch (e) {
            console.warn('[CustomPicker] matrix JSON parse failed', e);
            return map;
        }

        (Array.isArray(arr) ? arr : []).forEach((row) => {
            const keyArr = Array.isArray(row?.key) ? row.key : null;
            const url = String(row?.url || '').trim();
            const available = !!row?.available;

            if (!keyArr || !url) return;

            const key = this.#mkKey(keyArr);
            map.set(key, { url, keyArr, available });
        });

        return map;
    }
}

if (!customElements.get('custom-picker')) {
    customElements.define('custom-picker', CustomPicker);
}